/**
 * FreshnessIndicator.tsx — shows how old a snapshot is and flags staleness.
 *
 * Renders a localised relative age ("há 12 s" / "12s ago") from an ISO-8601
 * timestamp, plus a "stale" badge once the age exceeds `staleAfterSeconds`
 * (default ~35s = the spec's worst-case 15s cache + 20s poll). The relative
 * label is computed with locale-aware i18n strings, never a hand-rolled
 * `toFixed` / locale-less format.
 */

import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

export interface FreshnessIndicatorProps {
  /** ISO-8601 timestamp the snapshot was generated. */
  timestamp: string;
  /**
   * Server-authoritative serve-time staleness (HealthOverview.cache_age_seconds).
   * Preferred over the client-clock delta when present: it is computed on the
   * server, so it is immune to browser clock skew. Falls back to the timestamp
   * delta when absent.
   */
  cacheAgeSeconds?: number | null;
  /** "Now" reference, injectable for deterministic tests. Defaults to Date.now(). */
  nowMs?: number;
  /** Age beyond which the snapshot is flagged stale. */
  staleAfterSeconds?: number;
}

const ageSeconds = (timestamp: string, nowMs: number): number => {
  const then = Date.parse(timestamp);
  if (Number.isNaN(then)) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.floor((nowMs - then) / 1000));
};

export const FreshnessIndicator = ({
  timestamp,
  cacheAgeSeconds = undefined,
  nowMs = undefined,
  staleAfterSeconds = 35,
}: FreshnessIndicatorProps) => {
  const { t } = useTranslation();
  const reference = nowMs ?? Date.now();
  // Prefer the server's skew-free serve-time age; fall back to the client-clock
  // delta only when the server did not supply one.
  const seconds =
    cacheAgeSeconds != null && Number.isFinite(cacheAgeSeconds)
      ? Math.max(0, Math.floor(cacheAgeSeconds))
      : ageSeconds(timestamp, reference);
  const isStale = seconds > staleAfterSeconds;

  let relative: ReactNode;
  if (seconds < 5) {
    relative = t('freshness.justNow');
  } else if (seconds < 60) {
    relative = t('freshness.secondsAgo', { count: seconds });
  } else {
    relative = t('freshness.minutesAgo', { count: Math.floor(seconds / 60) });
  }

  return (
    <span
      data-testid="freshness-indicator"
      data-stale={isStale ? 'true' : 'false'}
      className="inline-flex items-center gap-2 text-meta text-ink-3"
    >
      <span>
        {t('freshness.label')}: {relative}
      </span>
      {isStale ? (
        <span className="rounded bg-status-degraded/15 px-1.5 py-0.5 text-status-degraded">
          {t('freshness.stale')}
        </span>
      ) : null}
    </span>
  );
};
